import React, { useEffect, useMemo, useState } from 'react';
import { connectorsApi } from '../services/connectorsApi';

const DEFAULT_FORM = {
  name: '',
  type: '',
  environment: 'prod',
  description: '',
  enabled: true,
  secrets: {},
  options: {},
};

const modalBackdropClasses =
  'fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8';
const modalContainerClasses =
  'bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl rounded';

const ConnectorConfigFormModal = ({
  open,
  onClose,
  onSubmit,
  catalog,
  initialData,
  mode = 'create',
}) => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [oracleMode, setOracleMode] = useState('service_name');

  const typeOptions = useMemo(() => {
    return (catalog || []).map((item) => ({
      value: item.type,
      label: item.label || item.type,
    }));
  }, [catalog]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (initialData) {
      setForm({
        name: initialData.name ?? '',
        type: initialData.type ?? '',
        environment: initialData.environment ?? 'prod',
        description: initialData.description ?? '',
        enabled: initialData.enabled ?? true,
        secrets: {},
        options: initialData.options ?? {},
      });
      if (initialData.type === 'oracle') {
        setOracleMode(initialData.options?.sid ? 'sid' : 'service_name');
      }
    } else {
      setForm(DEFAULT_FORM);
      setOracleMode('service_name');
    }
    setError(null);
    setSaving(false);
    setTesting(false);
    setTestResult(null);
  }, [open, initialData]);

  const heading = useMemo(() => {
    if (mode === 'edit') {
      return 'Modifier le connecteur configuré';
    }
    if (mode === 'duplicate') {
      return 'Dupliquer le connecteur configuré';
    }
    return 'Nouveau connecteur configuré';
  }, [mode]);

  const selectedTypeMeta = useMemo(() => {
    return (catalog || []).find((item) => item.type === form.type);
  }, [catalog, form.type]);

  const requiredSecrets = selectedTypeMeta?.required_secrets || [];
  const optionalSecrets = useMemo(() => {
    const base = selectedTypeMeta?.optional_secrets || [];
    if (selectedTypeMeta?.type === 'oracle') {
      return base.filter((s) => s !== 'service_name' && s !== 'sid');
    }
    return base;
  }, [selectedTypeMeta]);
  const optionalOptions = selectedTypeMeta?.optional_options || [];

  const requiredOptions = useMemo(() => {
    const base = selectedTypeMeta?.required_options || [];
    if (!selectedTypeMeta) {
      return base;
    }
    if (selectedTypeMeta.type === 'oracle') {
      return base.filter((option) => option !== 'schema');
    }
    return base;
  }, [selectedTypeMeta]);

  if (!open) {
    return null;
  }

  const handleChange = (field) => (event) => {
    const value = field === 'enabled' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSecretChange = (key) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, secrets: { ...prev.secrets, [key]: value } }));
  };

  const handleOptionChange = (key) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, options: { ...prev.options, [key]: value } }));
  };

  const getSanitizedValues = () => {
    let sanitizedSecrets = Object.fromEntries(
      Object.entries(form.secrets || {}).filter(([, value]) => value !== '' && value !== null && value !== undefined)
    );

    if (form.type === 'oracle') {
      if (oracleMode === 'service_name') {
        delete sanitizedSecrets.sid;
      } else {
        delete sanitizedSecrets.service_name;
      }
    }

    const sanitizedOptions = Object.fromEntries(
      Object.entries(form.options || {}).filter(([, value]) => value !== '' && value !== null && value !== undefined)
    );

    return { sanitizedSecrets, sanitizedOptions };
  };

  const buildPayload = ({ includeEmptySecrets, sanitizedSecrets, sanitizedOptions }) => {
    const secretsPayload = Object.keys(sanitizedSecrets).length > 0
      ? sanitizedSecrets
      : includeEmptySecrets
        ? {}
        : undefined;

    return {
      name: form.name.trim(),
      type: form.type,
      environment: form.environment,
      description: form.description.trim(),
      enabled: form.enabled,
      secrets: secretsPayload,
      options: sanitizedOptions,
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    const { sanitizedSecrets, sanitizedOptions } = getSanitizedValues();
    const payload = buildPayload({ includeEmptySecrets: mode !== 'edit', sanitizedSecrets, sanitizedOptions });

    if (!payload.name) {
      setError('Le nom est obligatoire.');
      return;
    }
    if (!payload.type) {
      setError('Le type est obligatoire.');
      return;
    }
    const missingSecrets = requiredSecrets.filter((key) => !sanitizedSecrets[key]);
    if (missingSecrets.length > 0 && mode !== 'edit') {
      setError(`Renseignez les secrets requis: ${missingSecrets.join(', ')}.`);
      return;
    }
    if (form.type === 'oracle' && mode !== 'edit') {
      if (!sanitizedSecrets[oracleMode]) {
        setError(`Renseignez le champ ${oracleMode === 'service_name' ? 'Service Name' : 'SID'}.`);
        return;
      }
    }

    const missingOptions = requiredOptions.filter((key) => !sanitizedOptions[key]);
    if (missingOptions.length > 0) {
      setError(`Renseignez les options requises: ${missingOptions.join(', ')}.`);
      return;
    }

    try {
      setSaving(true);
      await onSubmit(payload);
      setSaving(false);
      onClose();
    } catch (submissionError) {
      const detail =
        submissionError?.response?.data?.detail ||
        submissionError?.message ||
        "Une erreur est survenue lors de l'enregistrement.";
      setError(detail);
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setError(null);
    setTestResult(null);

    const { sanitizedSecrets, sanitizedOptions } = getSanitizedValues();
    const payload = buildPayload({ includeEmptySecrets: true, sanitizedSecrets, sanitizedOptions });

    if (!payload.type) {
      setError('Sélectionnez un type avant de tester.');
      return;
    }

    const missingSecrets = requiredSecrets.filter((key) => !sanitizedSecrets[key]);
    if (missingSecrets.length > 0) {
      setError(`Secrets manquants pour le test: ${missingSecrets.join(', ')}.`);
      return;
    }
    if (form.type === 'oracle') {
      if (!sanitizedSecrets[oracleMode]) {
        setError(`Renseignez le champ ${oracleMode === 'service_name' ? 'Service Name' : 'SID'}.`);
        return;
      }
    }

    try {
      setTesting(true);
      const response = await connectorsApi.testConfiguration(payload);
      setTestResult({ status: response.data.status, data: response.data });
    } catch (testError) {
      const detail =
        testError?.response?.data?.detail ||
        testError?.message ||
        'Erreur lors du test de connexion.';
      setTestResult({ status: 'error', data: { detail } });
    } finally {
      setTesting(false);
    }
  };
  return (
    <div className={modalBackdropClasses} onClick={onClose}>
      <div className={modalContainerClasses} onClick={(event) => event.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-900">{heading}</h2>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600"
            onClick={onClose}
            disabled={saving}
          >
            ✕
          </button>
        </div>

        <form className="px-6 py-6 space-y-5" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex flex-col text-sm text-slate-700">
              Nom *
              <input
                type="text"
                value={form.name}
                onChange={handleChange('name')}
                className="mt-1 px-3 py-2 border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#E20613]"
                placeholder="finance-oracle-prod"
              />
            </label>

            <label className="flex flex-col text-sm text-slate-700">
              Type *
              <select
                value={form.type}
                onChange={handleChange('type')}
                className="mt-1 px-3 py-2 border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#E20613]"
              >
                <option value="">Sélectionner un type</option>
                {typeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

          </div>

          <label className="flex flex-col text-sm text-slate-700">
            Description
            <textarea
              value={form.description}
              onChange={handleChange('description')}
              className="mt-1 px-3 py-2 border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#E20613]"
              rows="2"
              placeholder="Description courte"
            />
          </label>

          <div className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={handleChange('enabled')}
            />
            <span>Connecteur actif</span>
          </div>

          {selectedTypeMeta && (requiredSecrets.length > 0 || form.type === 'oracle') && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-800">Secrets requis</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {requiredSecrets.map((secret) => {
                  const inputType = secret.toLowerCase().includes('password') ? 'password' : 'text';
                  return (
                    <label key={secret} className="flex flex-col text-sm text-slate-700">
                      {secret}
                      <input
                        type={inputType === 'password' ? 'password' : 'text'}
                        value={form.secrets[secret] ?? ''}
                        onChange={handleSecretChange(secret)}
                        className="mt-1 px-3 py-2 border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#E20613]"
                        placeholder={`Secret ${secret}`}
                      />
                    </label>
                  );
                })}
              </div>
              {form.type === 'oracle' && (
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-700 font-medium">Mode de connexion :</span>
                    <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                      <input
                        type="radio"
                        name="oracleMode"
                        value="service_name"
                        checked={oracleMode === 'service_name'}
                        onChange={() => setOracleMode('service_name')}
                      />
                      Service Name
                    </label>
                    <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                      <input
                        type="radio"
                        name="oracleMode"
                        value="sid"
                        checked={oracleMode === 'sid'}
                        onChange={() => setOracleMode('sid')}
                      />
                      SID
                    </label>
                  </div>
                  <label className="flex flex-col text-sm text-slate-700">
                    {oracleMode === 'service_name' ? 'service_name' : 'sid'} *
                    <input
                      type="text"
                      value={form.secrets[oracleMode] ?? ''}
                      onChange={handleSecretChange(oracleMode)}
                      className="mt-1 px-3 py-2 border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#E20613]"
                      placeholder={oracleMode === 'service_name' ? 'ex: ORCL ou XE' : 'ex: ORCL'}
                    />
                  </label>
                </div>
              )}
              <p className="text-xs text-slate-500">
                Les secrets sont chiffrés dans Vault lors de l'enregistrement. Pour modifier un secret existant, saisissez une nouvelle valeur.
              </p>
            </div>
          )}

          {selectedTypeMeta && optionalSecrets.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-800">Secrets optionnels</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {optionalSecrets.map((secret) => {
                  const inputType = secret.toLowerCase().includes('password') ? 'password' : 'text';
                  return (
                    <label key={secret} className="flex flex-col text-sm text-slate-700">
                      {secret}
                      <input
                        type={inputType === 'password' ? 'password' : 'text'}
                        value={form.secrets[secret] ?? ''}
                        onChange={handleSecretChange(secret)}
                        className="mt-1 px-3 py-2 border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#E20613]"
                        placeholder={`Secret ${secret}`}
                      />
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500">Ces champs restent facultatifs et peuvent être complétés selon vos besoins.</p>
            </div>
          )}

          {selectedTypeMeta && requiredOptions.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-800">Paramètres requis</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {requiredOptions.map((option) => (
                  <label key={option} className="flex flex-col text-sm text-slate-700">
                    {option}
                    <input
                      type="text"
                      value={form.options[option] ?? ''}
                      onChange={handleOptionChange(option)}
                      className="mt-1 px-3 py-2 border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#E20613]"
                      placeholder={`Paramètre ${option}`}
                    />
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Paramètres optionnels et rappels retirés pour alléger le formulaire */}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            {testResult && (
              <div
                className={`flex-1 text-sm border px-3 py-2 ${
                  testResult.status === 'success'
                    ? 'bg-green-50 border-green-300 text-green-800'
                    : 'bg-red-50 border-red-300 text-red-700'
                }`}
              >
                {testResult.status === 'success'
                  ? '✅ Test de connexion réussi'
                  : `❌ ${testResult.data.detail || 'Test de connexion échoué'}`}
              </div>
            )}
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
              onClick={onClose}
              disabled={saving}
            >
              Annuler
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium border border-[#004A99] text-[#004A99] hover:bg-red-50 disabled:opacity-60"
              onClick={handleTest}
              disabled={saving || testing}
            >
              {testing ? 'Test…' : 'Tester la connexion'}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#E20613] text-white hover:bg-[#c90511] text-sm font-medium disabled:opacity-60"
              disabled={saving}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConnectorConfigFormModal;
